import { NgModule } from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common'
import { GoogleMapsModule } from '@angular/google-maps';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientJsonpModule, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { SignalRModule, SignalRConfiguration } from '@henkkelder/ng2-signalr';
import { MatTreeModule } from '@angular/material/tree';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule, MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MatFormFieldModule, MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatAutocompleteModule, MAT_AUTOCOMPLETE_SCROLL_STRATEGY } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatBadgeModule } from '@angular/material/badge';
import { BidiModule } from '@angular/cdk/bidi';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule } from '@angular/material/table';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatRadioModule } from '@angular/material/radio';
import { MtxDatetimepickerModule } from '@ng-matero/extensions/datetimepicker';
import { MtxNativeDatetimeModule } from '@ng-matero/extensions/core';
import { MomentDateModule } from '@angular/material-moment-adapter';
import { MomentDatetimeModule } from '@ng-matero/extensions-moment-adapter';
import { MatPaginatorModule } from '@angular/material/paginator';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatRippleModule} from '@angular/material/core';

import { EnpsComponent } from '@common/components/base/enps-component';
import { EnpsRouterComponent } from '@common/components/base/enps-router-component'
import { EnpsOnPushComponent } from '@common/components/base/enps-onpush-component';
import { EnpsSinglePageComponent } from '@common/components/base/enps-single-page'
import { EnpsControl } from '@common/controls/enps-control/enps-control';
import { CreateNewView } from '@common/components/create-new-view/create-new-view';
import { ViewState } from '@common/components/view-state/view-state';
import { PlaybookViewState } from '@common/components/view-state/playbook-view-state';
import { DiscoverViewState } from '@common/components/view-state/discover-view-state';
import { CastViewState } from '@common/components/view-state/cast-view-state';

import { CreateNewTemplate } from '@common/components/create-new-template/create-new-template';
import { TimeControl } from '@common/controls/time-control/time-control';
import { Link } from '@common/components/navigation/link/link';
import { LinkBack } from '@common/components/navigation/link-back/link-back';
import { TextField } from '@common/controls/text-field/text-field';
import { TextFieldCurrency } from '@common/controls/text-field/text-field-currency/text-field-currency';
import { ComboBox } from '@common/controls/combo-box/combo-box';
import { Special } from '@common/controls/special/special';
import { AddressForm } from '@common/controls/address-form/address-form';
import { SpecialCost } from '@common/controls/special/special-cost/special-cost';
import { CheckList, CheckListItemsDirective } from '@common/controls/check-list/check-list';
import { CheckBox } from '@common/controls/check-box/check-box';
import { StTextArea } from '@common/controls/st-text-area/st-text-area';
import { DatePicker } from '@common/controls/date-picker/date-picker';
import { StExpansionPanel } from '@common/components/st-expansion-panel/st-expansion-panel.component';
import { StAccordion } from '@common/components/st-expansion-panel/st-accordion.component';
import { LoaderModule } from '@loader/loader.module';
import { Filter } from '@common/components/filter/filter/filter';
import { FilterList } from '@common/components/filter/filter-list/filter-list';
import { FilterPlanningViews } from '@common/components/filter/filter-planning-views/filter-planning-views';
import { FilterManageTeamMembers } from '@common/components/filter/filter-manage-team-members/filter-manage-team-members';
import { ViewstatesList } from '@common/components/filter/viewstates-list/viewstates-list';
import { CastObjects } from '@src/suite/common/components/cast-objects/cast-objects';
import { CastObjectItem } from '@src/suite/common/components/cast-object-item/cast-object-item';
import { ListDropDown } from '@common/components/list-drop-down/list-drop-down';
import { ExternalActionButton } from '@common/components/external-action-button/external-action-button';
import { AvatarBig, AvatarBigHorizontalName, AvatarBigVerticalName } from '@common/components/avatar/avatar-big';
import { AvatarSmall, AvatarSmallHorizontalName, AvatarSmallVerticalName } from '@common/components/avatar/avatar-small';
import { Avatar } from '@common/components/avatar/avatar-upload';
import { Autocomplete } from '@common/controls/autocomplete/autocomplete';
import { StaffIDList } from '@common/controls/staff-id-list/staff-id-list';
import { StaffIDListTooltip } from '@common/components/staff-id-list-tooltip/staff-id-list-tooltip';
import { TagsAutocomplete } from '@common/controls/tags/tags-autocomplete';
import { HighlightPipe } from '@common/pipes/highlight';
import { TopicTypePipe } from '@common/pipes/topic-type';
import { FormattedTextPipe } from '@common/pipes/formatted-text';
import { UnsafePipe } from '@common/pipes/unsafe'
import { SortPipe } from '@common/pipes/sort';
import { OnlyNumber } from '@common/directives/only-number';
import { Modal } from '@common/components/modal/modal/modal';
import { AlertModal } from '@common/components/modal/alert/alert';
import { FatalModal } from '@common/components/modal/fatal/fatal';
import { ErrorModal } from '@common/components/modal/error/error';
import { Icon } from "@common/components/icon/icon";
import { DateRangePicker } from "@common/controls/date-range-picker/date-range-picker";
import { DateRangePager } from "@common/components/date-range-pager/date-range-pager";
import { DateTimeRange } from "@common/controls/date-time-range/date-time-range";
import { StaffIDListMultiple } from '@common/controls/staff-id-list/staff-id-list-multiple';
import { AttachmentsList } from '@common/components/attachments-list/attachments-list'
import { LinkyModule } from 'ngx-linky';
import { OAuthModule } from 'angular-oauth2-oidc';
import { CurrencyMaskModule } from "ng2-currency-mask";
import { EnpsDroppable } from "@common/directives/enps.droppable";
import { AttachmentRenameComponent } from '@common/components/attachments-list/modal/rename/attachment-rename';
import { AttachmentErrorComponent } from '@common/components/attachments-list/modal/error/attachment-error';
import { Pager } from "@common/components/pager/pager";
import { NgxUploaderModule } from '@angular-ex/uploader';
import { RouterModule } from '@angular/router';

import { List } from '@common/components/base/list';
import { HeaderDirective } from '@common/directives/header-directive';
import { FooterDirective } from '@common/directives/footer-directive';
import { ItemDirective } from '@common/directives/item-directive';
import { TileColumnComponent } from '@common/components/tile-column/tile-column';
import { TileGroupColumnComponent } from '@common/components/tile-group-column/tile-grop-column';
import { TileView } from '@common/components/tile-view/tile-view';
import { MapView } from '@common/components/map-view/map-view';

import { SearchBox } from '@common/components/search-box/search-box';

import { AssignmentIconDirective } from '@suite/common/directives/assignment-icon';
import { TopicCardTypeColorDirective } from '@common/directives/topic-card-type-color';
import { CardLeftBorderDirective } from '@common/directives/card-left-border';
import { AbstractAdornerDirective } from '@common/directives/abstract-adorner';
import { TopicIconDirective } from '@common/directives/topic-icon';
import { ClickStopPropagation } from '@common/directives/stop-propagation';

import { ScrollingModule } from '@angular/cdk/scrolling';
import { Overlay, OverlayModule, RepositionScrollStrategy } from '@angular/cdk/overlay';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { GenericAttributeDirective } from './directives/generic-attribute.directive';
import { StTopicItem } from '@common/components/topic-item/st-topic-item.component';
import { TopicItemCondensed } from '@common/components/topic-item-condensed/topic-item-condensed';
import { TopicType } from '@common/controls/topic-type/topic-type';
import { Assignment } from '@common/components/assignments-list/assignment/assignment';
import { StAssignmentCondensed } from '@common/components/assignments-list/st-assignment-condensed/st-assignment-condensed';
import { AssignmentType } from '@common/controls/assignment-type/assignment-type';
import { Status } from '@common/controls/status/status';
import { Desk } from '@common/controls/desk/desk';
import { ClipboardSourceChooserComponent } from '@suite/platform/components/clipboard/modal/clipboard-source-chooser-modal.component';
import { JwtIncerceptor } from '@common/services/interceptors/http-interceptor';
import { TreeChecklist } from '@common/controls/tree-checklist/tree-checklist';
import { ToggleControl } from '@common/controls/toggle-button/toggle-control';
import { Tree } from '@common/controls/tree/tree';
import { DifferentSizesVirtualScrollDirective } from '@common/directives/different-sizes-virtual-scroll';
import { StTextAreaOverlay } from '@common/controls/st-text-area/text-area-overlay/st-text-area-overlay';
import { Dialog } from '@angular/cdk/dialog';
import { LayoutModule } from '@angular/cdk/layout';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipModule } from '@angular/material/tooltip';
import { A11yModule } from '@angular/cdk/a11y';
import { TopBarUserMenuDialogAbout } from '@platform/components/top-bar-user-menu/top-bar-user-menu-dialog-about';
import { ExternalActionsOverlay } from '@playbook/components/external-actions-overlay/external-actions-overlay'
import { AssignmentsOverlay } from '../playbook/components/assignments-overlay/assignments-overlay';
import { DateTimePicker } from './controls/date-time-picker/date-time-picker';

export function getSignalRConfiguration() {
    let signalrConfiguration = new SignalRConfiguration();
    return signalrConfiguration;
}

export function scrollFactory(overlay: Overlay): () => RepositionScrollStrategy {
    return () => overlay.scrollStrategies.reposition();
}


@NgModule({
    declarations: [
        EnpsComponent,
        EnpsOnPushComponent,
        EnpsControl,
        EnpsRouterComponent,
        EnpsSinglePageComponent,
        TimeControl,
        Link,
        LinkBack,
        StExpansionPanel,
        StAccordion,
        TextField,
        StTextAreaOverlay,
        TextFieldCurrency,
        DatePicker,
        StTextArea,
        ComboBox,
        CheckList,
        CheckBox,
        ToggleControl,
        Special,
        Tree,
        TreeChecklist,
        Icon,
        SpecialCost,
        DateRangePicker,
        DateRangePager,
        DateTimeRange,
        Pager,
        Modal,
        AlertModal,
        FatalModal,
        ErrorModal,
        ViewState,
        PlaybookViewState,
        DiscoverViewState,
        CastViewState,
        CreateNewView,
        CreateNewTemplate,
        ExternalActionButton,
        Avatar,
        AvatarBig,
        AvatarBigHorizontalName,
        AvatarBigVerticalName,
        AvatarSmallHorizontalName,
        AvatarSmallVerticalName,
        AvatarSmall,
        ListDropDown,
        TileColumnComponent,
        TileGroupColumnComponent,
        TileView,
        Filter,
        FilterList,
        FilterPlanningViews,
        FilterManageTeamMembers,
        ViewstatesList,
        CastObjects,
        CastObjectItem,
        Autocomplete,
        StaffIDList,
        StaffIDListTooltip,
        TagsAutocomplete,
        HighlightPipe,
        FormattedTextPipe,
        SortPipe,
        OnlyNumber,
        UnsafePipe,
        TopicTypePipe,
        StaffIDListMultiple,
        AddressForm,
        AttachmentsList,
        EnpsDroppable,
        AttachmentRenameComponent,
        AttachmentErrorComponent,
        List,
        MapView,
        StTopicItem,
        TopicItemCondensed,
        Status,
        Desk,
        TopicType,
        Assignment,
        StAssignmentCondensed,
        AssignmentType,
        HeaderDirective,
        ItemDirective,
        FooterDirective,
        SearchBox,
        AbstractAdornerDirective,
        CardLeftBorderDirective,
        AssignmentIconDirective,
        TopicCardTypeColorDirective,
        TopicIconDirective,
        GenericAttributeDirective,
        ClipboardSourceChooserComponent,
        ClickStopPropagation,
        DifferentSizesVirtualScrollDirective,
        CheckListItemsDirective,
        TopBarUserMenuDialogAbout,
        ExternalActionsOverlay,
        AssignmentsOverlay,
        DateTimePicker
    ],
    imports: [
        RouterModule,
        FormsModule,
        CommonModule,
        HttpClientModule,
        HttpClientJsonpModule,
        ReactiveFormsModule,
        NgxUploaderModule,
        SignalRModule.forRoot(getSignalRConfiguration),
        DragDropModule,
        CurrencyMaskModule,
        // TreeModule,
        MatTreeModule,
        MatCheckboxModule,
        MatSidenavModule,
        MatSlideToggleModule,
        MatBadgeModule,
        BidiModule,
        MatChipsModule,
        MatTabsModule,
        MatTableModule,
        OAuthModule.forRoot(),
        MatMenuModule,
        MatIconModule,
        MatButtonModule,
        MatSelectModule,
        MatToolbarModule,
        // MatNativeDateModule,
        MatInputModule,
        MatDialogModule,
        MatListModule,
        MatAutocompleteModule,
        MatFormFieldModule,
        MatDatepickerModule,
        MatButtonToggleModule,
        MatGridListModule,
        MatRadioModule,
        MtxDatetimepickerModule,
        MtxNativeDatetimeModule,
        MomentDatetimeModule,
        MomentDateModule,
        MatPaginatorModule,
        GoogleMapsModule,
        MatProgressBarModule,
        MatRippleModule,
        LinkyModule,
        LoaderModule,
        OverlayModule,
        MatExpansionModule,
        NgOptimizedImage,
        ScrollingModule,
        MatTooltipModule,
        LayoutModule,
        A11yModule
    ],
    providers: [
        { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: { position: 'above', showDelay: 1000 } },
        { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { hasBackdrop: false } },
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { floatLabel: 'auto', appearance: 'outline' } },
        { provide: MAT_AUTOCOMPLETE_SCROLL_STRATEGY, useFactory: scrollFactory, deps: [Overlay] }, {
            provide: HTTP_INTERCEPTORS,
            useClass: JwtIncerceptor,
            multi: true
        },
        Dialog,
        HighlightPipe
    ],
    exports: [
        RouterModule,
        FormsModule,
        OAuthModule,
        CommonModule,
        DragDropModule,
        HttpClientModule,
        HttpClientJsonpModule,
        ReactiveFormsModule,
        NgxUploaderModule,
        CurrencyMaskModule,
        MatMenuModule,
        MatIconModule,
        MatButtonModule,
        MatSelectModule,
        OverlayModule,
        MatTreeModule,
        MatToolbarModule,
        MatCheckboxModule,
        MatChipsModule,
        MatTabsModule,
        MatTableModule,
        MatSlideToggleModule,
        MatBadgeModule,
        BidiModule,
        TileColumnComponent,
        TileGroupColumnComponent,
        // MatNativeDateModule,
        MatInputModule,
        MatDialogModule,
        MatListModule,
        MatSidenavModule,
        MatAutocompleteModule,
        MatFormFieldModule,
        MatDatepickerModule,
        MatButtonToggleModule,
        MatGridListModule,
        MatRadioModule,
        MtxDatetimepickerModule,
        GoogleMapsModule,
        MatPaginatorModule,
        MatProgressBarModule,
        MatRippleModule,
        LinkyModule,
        LoaderModule,
        EnpsComponent,
        EnpsOnPushComponent,
        EnpsControl,
        EnpsRouterComponent,
        EnpsSinglePageComponent,
        TimeControl,
        SearchBox,
        Link,
        LinkBack,
        StTextAreaOverlay,
        TextField,
        TextFieldCurrency,
        DatePicker,
        StTextArea,
        ComboBox,
        CheckList,
        CheckBox,
        Special,
        Tree,
        TreeChecklist,
        Icon,
        SpecialCost,
        DateRangePicker,
        DateRangePager,
        DateTimeRange,
        Pager,
        StAccordion,
        StExpansionPanel,
        Modal,
        AlertModal,
        FatalModal,
        ErrorModal,
        ViewState,
        PlaybookViewState,
        DiscoverViewState,
        CastViewState,
        CreateNewView,
        CreateNewTemplate,
        ToggleControl,
        ExternalActionButton,
        Avatar,
        AvatarBig,
        AvatarBigHorizontalName,
        AvatarBigVerticalName,
        AvatarSmall,
        AvatarSmallHorizontalName,
        AvatarSmallVerticalName,
        ListDropDown,
        TileView,
        MapView,
        Filter,
        FilterList,
        FilterPlanningViews,
        FilterManageTeamMembers,
        ViewstatesList,
        CastObjects,
        CastObjectItem,
        Autocomplete,
        StaffIDList,
        StaffIDListTooltip,
        TagsAutocomplete,
        HighlightPipe,
        FormattedTextPipe,
        SortPipe,
        OnlyNumber,
        UnsafePipe,
        TopicTypePipe,
        StaffIDListMultiple,
        AddressForm,
        AttachmentsList,
        EnpsDroppable,
        AttachmentRenameComponent,
        AttachmentErrorComponent,
        HeaderDirective,
        GenericAttributeDirective,
        ItemDirective,
        FooterDirective,
        AbstractAdornerDirective,
        CardLeftBorderDirective,
        AssignmentIconDirective,
        TopicCardTypeColorDirective,
        TopicIconDirective,
        StTopicItem,
        TopicItemCondensed,
        MatTooltipModule,
        Status,
        Desk,
        TopicType,
        Assignment,
        StAssignmentCondensed,
        AssignmentType,
        ClipboardSourceChooserComponent,
        MatExpansionModule,
        ClickStopPropagation,
        NgOptimizedImage,
        ScrollingModule,
        LayoutModule,
        DifferentSizesVirtualScrollDirective,
        CheckListItemsDirective,
        A11yModule,
        TopBarUserMenuDialogAbout,
        ExternalActionsOverlay,
        AssignmentsOverlay,
        DateTimePicker
    ],
})
export class StorytellingCommonModule { }
